import pickBy from 'lodash/pickBy'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  coatingThicknessFormField,
  compassOrientationFormField,
  installationDateFormField,
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoManway,
  GeckoManwayJSON,
} from 'types/assetModel/features/geckoManway'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import {
  GeckoPolygonalPlate,
  GeckoPolygonalPlateJSON,
} from 'types/assetModel/features/geckoPolygonalPlate'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { getFormattedInstallationDateString } from 'utils/installationDateHelpers'
import { mergeMaps } from 'utils/mergeMaps'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export type SphereFlatCapAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.SphereFlatCap
  radius: number
  features: Record<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPlateJSON
    | GeckoPolygonalPlateJSON
    | GeckoNozzleJSON
    | GeckoManwayJSON
  >
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  tmin: number | undefined
  nominal: {
    wall_thickness: number | undefined
  }
  compass_orientation: number | undefined
  material: string | undefined
  installation_date: string | undefined
  lining_type: string | undefined
  coating_type: string | undefined
  coating_thickness: number | undefined
  insulation_type: string | undefined
  insulation_thickness: number | undefined
  joint_efficiency: number | undefined
  stress_tolerance: number | undefined
  design_pressure: number | undefined
}

export const SphereFlatCapAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.Plate,
  GeckoFeatureModelType.PolygonalPlate,
  GeckoFeatureModelType.VerticalWeld,
  GeckoFeatureModelType.HorizontalWeld,
  GeckoFeatureModelType.Nozzle,
  GeckoFeatureModelType.Manway,
]

export const NewSphereFlatCapAssetModelJSON = {
  name: 'New Sphere Flat Cap',
  type: GeckoComponentModelType.SphereFlatCap,
  radius: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  nominal: {
    wall_thickness: undefined,
  },
  compass_orientation: undefined,
  material: undefined,
  installation_date: undefined,
  lining_type: undefined,
  coating_type: undefined,
  coating_thickness: undefined,
  insulation_type: undefined,
  insulation_thickness: undefined,
  joint_efficiency: undefined,
  stress_tolerance: undefined,
  design_pressure: undefined,
} as SphereFlatCapAssetModelJSON

export class GeckoSphereFlatCap extends GeckoEditableComponent {
  type: GeckoComponentModelType.SphereFlatCap
  radius: GeckoMeasurement
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  plates: Map<string, GeckoPlate>
  polygonalPlates: Map<string, GeckoPolygonalPlate>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  verticalWelds: Map<string, GeckoVerticalWeld>
  nozzles: Map<string, GeckoNozzle>
  manways: Map<string, GeckoManway>
  features: Map<
    string,
    | GeckoPlate
    | GeckoPolygonalPlate
    | GeckoHorizontalWeld
    | GeckoVerticalWeld
    | GeckoNozzle
    | GeckoManway
  >
  wallNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  compassOrientation: number | undefined
  material: string | undefined
  installationDate: Date | undefined
  liningType: string | undefined
  coatingType: string | undefined
  coatingThickness: GeckoMeasurement | undefined
  insulationType: string | undefined
  insulationThickness: GeckoMeasurement | undefined
  jointEfficiency: number | undefined
  stressTolerance: GeckoMeasurement | undefined
  designPressure: GeckoMeasurement | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    plates: Map<string, GeckoPlate>,
    polygonalPlates: Map<string, GeckoPolygonalPlate>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    nozzles: Map<string, GeckoNozzle>,
    manways: Map<string, GeckoManway>,
    wallNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    compassOrientation: number | undefined,
    material: string | undefined,
    installationDate: Date | undefined,
    liningType: string | undefined,
    coatingType: string | undefined,
    coatingThickness: GeckoMeasurement | undefined,
    insulationType: string | undefined,
    insulationThickness: GeckoMeasurement | undefined,
    jointEfficiency: number | undefined,
    stressTolerance: GeckoMeasurement | undefined,
    designPressure: GeckoMeasurement | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.SphereFlatCap
    this.radius = radius
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.wallNominalThickness = wallNominalThickness
    this.tmin = tmin
    this.compassOrientation = compassOrientation
    this.material = material
    this.installationDate = installationDate
    this.liningType = liningType
    this.coatingType = coatingType
    this.coatingThickness = coatingThickness
    this.insulationType = insulationType
    this.insulationThickness = insulationThickness
    this.jointEfficiency = jointEfficiency
    this.stressTolerance = stressTolerance
    this.designPressure = designPressure

    // Features
    this.plates = plates
    this.polygonalPlates = polygonalPlates
    this.verticalWelds = verticalWelds
    this.horizontalWelds = horizontalWelds
    this.nozzles = nozzles
    this.manways = manways

    this.features = mergeMaps(
      plates,
      polygonalPlates,
      horizontalWelds,
      verticalWelds,
      nozzles,
      manways
    )
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get polygonalPlatesArray() {
    return Array.from(this.polygonalPlates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get manwaysArray() {
    return Array.from(this.manways.values())
  }

  public static CreateFromJSON(id: string, json: SphereFlatCapAssetModelJSON) {
    const name = json.name as string
    const radius = GeckoMeasurement.CreateFromValue(
      json.radius as number,
      GeckoMeasurementUnit.Meters
    )

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const featureData = json.features as Record<
      string,
      | GeckoPlateJSON
      | GeckoPolygonalPlateJSON
      | GeckoHorizontalWeldJSON
      | GeckoVerticalWeldJSON
      | GeckoManwayJSON
      | GeckoNozzleJSON
    >

    const childPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Plate
    })
    const plates = GeckoPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoPlateJSON>
    )

    const childPolygonalPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PolygonalPlate
    })
    const polygonalPlates = GeckoPolygonalPlate.CreateMapFromJSON(
      id,
      childPolygonalPlateJSON as Record<string, GeckoPolygonalPlateJSON>
    )

    const childHorizontalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const childNozzleJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    })
    const nozzles = GeckoNozzle.CreateMapFromJSON(
      id,
      childNozzleJSON as Record<string, GeckoNozzleJSON>
    )

    const childManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Manway
    })
    const manways = GeckoManway.CreateMapFromJSON(
      id,
      childManwayJSON as Record<string, GeckoManwayJSON>
    )

    const nominalJSON = json.nominal as Record<string, number>
    const parsedWallNominalThickness = parseNumber(nominalJSON.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    const parsedTmin = parseNumber(json.tmin)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    const compassOrientation = parseNumber(json.compass_orientation)
    const material = parseString(json.material)
    const installationDate = parseDate(json.installation_date)
    const liningType = parseString(json.lining_type)
    const coatingType = parseString(json.coating_type)
    const parsedCoatingThickness = parseNumber(json.coating_thickness)
    const coatingThickness =
      parsedCoatingThickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedCoatingThickness,
            GeckoMeasurementUnit.Millimeters
          )
        : undefined
    const insulationType = parseString(json.insulation_type)
    const parsedInsulationThickness = parseNumber(json.insulation_thickness)
    const insulationThickness =
      parsedInsulationThickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedInsulationThickness,
            GeckoMeasurementUnit.Millimeters
          )
        : undefined
    const jointEfficiency = parseNumber(json.joint_efficiency)
    const parsedStressTolerance = parseNumber(json.stress_tolerance)
    const stressTolerance =
      parsedStressTolerance !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedStressTolerance,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined
    const parsedDesignPressure = parseNumber(json.design_pressure)
    const designPressure =
      parsedDesignPressure !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedDesignPressure,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined

    return new GeckoSphereFlatCap(
      id,
      json,
      name,
      radius,
      componentBounds,
      sceneConfig,
      plates,
      polygonalPlates,
      horizontalWelds,
      verticalWelds,
      nozzles,
      manways,
      wallNominalThickness,
      tmin,
      compassOrientation,
      material,
      installationDate,
      liningType,
      coatingType,
      coatingThickness,
      insulationType,
      insulationThickness,
      jointEfficiency,
      stressTolerance,
      designPressure
    )
  }

  public asJSON(): SphereFlatCapAssetModelJSON {
    return generateSphereFlatCap(
      this.name,
      this.radius,
      this.wallNominalThickness,
      this.tmin,
      this.compassOrientation,
      this.material,
      this.installationDate,
      this.liningType,
      this.coatingType,
      this.coatingThickness,
      this.insulationType,
      this.insulationThickness,
      this.jointEfficiency,
      this.stressTolerance,
      this.designPressure,
      this.json as SphereFlatCapAssetModelJSON
    )
  }
}

const generateSphereFlatCap = (
  name: string,
  radius: GeckoMeasurement,
  wallNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  material: string | undefined,
  installationDate: Date | undefined,
  liningType: string | undefined,
  coatingType: string | undefined,
  coatingThickness: GeckoMeasurement | undefined,
  insulationType: string | undefined,
  insulationThickness: GeckoMeasurement | undefined,
  jointEfficiency: number | undefined,
  stressTolerance: GeckoMeasurement | undefined,
  designPressure: GeckoMeasurement | undefined,
  otherSphereFlatCapData: SphereFlatCapAssetModelJSON = NewSphereFlatCapAssetModelJSON
): SphereFlatCapAssetModelJSON => {
  const capRadiusMeters = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  // For center-based coordinate system
  const negativeRadiusMeters = -capRadiusMeters

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          ?.toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          ?.toPrecision(assetModelPrecision).num
      : undefined

  const coatingThicknessMeters =
    coatingThickness !== undefined
      ? coatingThickness
          .convertTo(GeckoMeasurementUnit.Millimeters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const insulationThicknessMeters =
    insulationThickness !== undefined
      ? insulationThickness
          .convertTo(GeckoMeasurementUnit.Millimeters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedStressTolerance =
    stressTolerance !== undefined
      ? stressTolerance
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedDesignPressure =
    designPressure !== undefined
      ? designPressure
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  return {
    name: name,
    type: GeckoComponentModelType.SphereFlatCap,
    radius: capRadiusMeters,
    features: otherSphereFlatCapData.features,
    scene_config: otherSphereFlatCapData.scene_config,
    component_bounds: {
      x_start: negativeRadiusMeters,
      y_start: negativeRadiusMeters,
      x_end: capRadiusMeters,
      y_end: capRadiusMeters,
    },
    tmin: tminMeters,
    nominal: { wall_thickness: wallNominalThicknessMeters },
    compass_orientation: compassOrientation,
    material: material,
    installation_date: installationDate
      ? getFormattedInstallationDateString(installationDate)
      : undefined,
    lining_type: liningType,
    coating_type: coatingType,
    coating_thickness: coatingThicknessMeters,
    insulation_type: insulationType,
    insulation_thickness: insulationThicknessMeters,
    joint_efficiency: jointEfficiency,
    stress_tolerance: parsedStressTolerance,
    design_pressure: parsedDesignPressure,
  }
}

export const SphereFlatCapAssetModelFormFields = (
  sphereFlatCapJSON: SphereFlatCapAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Sphere Flat Cap Name',
      value: sphereFlatCapJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    // Diameter is the industry standard measurement, so convert to radius and back
    {
      dataKey: 'radius',
      label: 'Diameter',
      value: sphereFlatCapJSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    wallThicknessFormField(sphereFlatCapJSON.nominal.wall_thickness),
    {
      dataKey: 'joint_efficiency',
      label: 'Joint Efficiency',
      value: sphereFlatCapJSON.joint_efficiency,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'stress_tolerance',
      label: 'Stress Tolerance',
      value: sphereFlatCapJSON.stress_tolerance,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    tminFormField(sphereFlatCapJSON.tmin),
    {
      dataKey: 'design_pressure',
      label: 'Design Pressure',
      value: sphereFlatCapJSON.design_pressure,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material Type',
      value: sphereFlatCapJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(sphereFlatCapJSON.installation_date, true),
    {
      dataKey: 'lining_type',
      label: 'Lining Type',
      value: sphereFlatCapJSON.lining_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coating_type',
      label: 'Coating Type',
      value: sphereFlatCapJSON.coating_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    coatingThicknessFormField(sphereFlatCapJSON.coating_thickness),
    {
      dataKey: 'insulation_type',
      label: 'Insulation Type',
      value: sphereFlatCapJSON.insulation_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'insulation_thickness',
      label: 'Insulation Thickness',
      value: sphereFlatCapJSON.insulation_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    compassOrientationFormField(sphereFlatCapJSON.compass_orientation),
  ]
}

export const SphereFlatCapJSONFromFormFields = (
  component: SphereFlatCapAssetModelJSON,
  formFields: FormField[]
) => {
  const name = getFormFieldByDataKey(formFields, 'name')?.value ?? ''
  const radius = getFormFieldByDataKey(formFields, 'radius')
  const wallNominalThickness = getFormFieldByDataKey(
    formFields,
    'wall_thickness'
  )
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  const tmin = getFormFieldByDataKey(formFields, 'tmin')
  const parsedJointEfficiency = parseNumber(
    getFormFieldByDataKey(formFields, 'joint_efficiency')?.value
  )
  const parsedStressTolerance = parseNumber(
    getFormFieldByDataKey(formFields, 'stress_tolerance')?.value
  )
  const parsedDesignPressure = parseNumber(
    getFormFieldByDataKey(formFields, 'design_pressure')?.value
  )

  return generateSphereFlatCap(
    name as string,
    GeckoMeasurement.CreateFromValue(
      (radius?.value as number) / 2, //radius is diameter
      GeckoMeasurementUnit.Meters
    ),
    wallNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          wallNominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    tmin?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          tmin?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    compassOrientation,
    parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    getFormFieldByDataKey(formFields, 'installation_date')?.value
      ? new Date(
          parseString(
            getFormFieldByDataKey(formFields, 'installation_date')?.value
          ) ?? ''
        )
      : undefined,
    parseString(getFormFieldByDataKey(formFields, 'lining_type')?.value),
    parseString(getFormFieldByDataKey(formFields, 'coating_type')?.value),
    getFormFieldByDataKey(formFields, 'coating_thickness')?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parseNumber(
            getFormFieldByDataKey(formFields, 'coating_thickness')?.value
          ) as number,
          GeckoMeasurementUnit.Millimeters
        )
      : undefined,
    parseString(getFormFieldByDataKey(formFields, 'insulation_type')?.value),
    getFormFieldByDataKey(formFields, 'insulation_thickness')?.value !==
      undefined
      ? GeckoMeasurement.CreateFromValue(
          parseNumber(
            getFormFieldByDataKey(formFields, 'insulation_thickness')?.value
          ) as number,
          GeckoMeasurementUnit.Millimeters
        )
      : undefined,
    parsedJointEfficiency,
    parsedStressTolerance !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedStressTolerance,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    parsedDesignPressure !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedDesignPressure,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    component
  )
}
